import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LandingPage from './pages/LandingPage';
import WelcomePage from './pages/WelcomePage';
import BusinessRegistrationPage from './pages/BusinessRegistrationPage';
import LoginPage from './pages/LoginPage';
import CalendarPage from './pages/CalendarPage';
import MyBookingsPage from './pages/MyBookingsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { isBrowsing } from './utils/welcome';
import OfflineBanner from './components/ui/OfflineBanner';
import InstallPrompt from './components/ui/InstallPrompt';
import NativeShell from './components/NativeShell';
import { isNative } from './utils/platform';

const Router = isNative ? HashRouter : BrowserRouter;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const LOGIN_RETURN_KEY = 'loginReturnTo';

function ProtectedRoute({ children }) {
  const { isAuthenticated, authChecked } = useAuth();
  const location = useLocation();

  if (!authChecked) {
    return <div className="page-loading">جاري التحميل...</div>;
  }

  if (!isAuthenticated) {
    sessionStorage.setItem(LOGIN_RETURN_KEY, JSON.stringify({
      pathname: location.pathname,
      search: location.search || ''
    }));
    return <Navigate to="/login" replace />;
  }
  return children;
}

const RESERVED_PATHS = new Set([
  'login',
  'calendar',
  'bookings',
  'static',
  'files',
  'privacy',
  'contact',
  'welcome',
  'register-business',
  'sitemap.xml',
  'robots.txt',
  'manifest.json',
  'asset-manifest.json',
  'service-worker.js',
  'favicon.ico',
  'favicon.png',
  'index.html',
]);

function BusinessRedirect() {
  const { businessId } = useParams();
  const { search } = useLocation();
  const id = (businessId || '').toLowerCase();

  // Salon IDs are UUID-like; never treat static filenames (e.g. sitemap.xml) as businesses.
  if (!id || RESERVED_PATHS.has(id) || id.includes('.')) {
    return <Navigate to="/" replace />;
  }

  const query = new URLSearchParams(search);
  query.set('businessid', businessId);
  return <Navigate to={`/?${query.toString()}`} replace />;
}

function LandingPageWrapper() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const businessId = searchParams.get('businessid') || '';

  // The welcome/landing page is the default entry point of the site. Guests
  // hitting the bare home are sent there unless they've chosen to browse salons
  // in this session. Business links and logged-in users go straight to the app.
  if (!businessId && !isAuthenticated && !isBrowsing()) {
    return <Navigate to="/welcome" replace />;
  }

  return <LandingPage key={businessId} />;
}

function AppContent() {
  const [retryKey, setRetryKey] = React.useState(0);

  return (
    <>
      <OfflineBanner onRetry={() => setRetryKey((k) => k + 1)} />
      <Routes key={retryKey}>
        <Route path="/" element={<LandingPageWrapper />} />
        <Route path="/welcome" element={<WelcomePage />} />
        <Route path="/register-business" element={<BusinessRegistrationPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/calendar"
          element={
            <ProtectedRoute>
              <CalendarPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bookings"
          element={
            <ProtectedRoute>
              <MyBookingsPage />
            </ProtectedRoute>
          }
        />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/:businessId" element={<BusinessRedirect />} />
      </Routes>
      <InstallPrompt />
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <NativeShell />
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
